import {
  searchAdditionalCharge,
  searchLocationWithTransaction,
  searchStaffWithTransaction,
} from "apis";
import {
  FilterBar,
  FilterDateRange,
  FilterMultiSelect,
  FilterTimeRange,
} from "components/commons";
import { useApi, useMount } from "hooks";
import {
  locationWithTransactionFilterResponse,
  locationWithTransactionFilterRequest,
  staffWithTransactionFilterRequest,
  staffWithTransactionFilterResponse,
  categoryWithTransactionFilterRequest,
  additionalChargeResponseMapper,
} from "mappers";
import React, { useCallback, useEffect, useMemo } from "react";
import { mapObject, mapObjectsToSelect } from "services";
import lang from "translations";

const AdditionalChargeReportFilter = ({
  filterState,
  requestState,
  modifyFilter,
  clearFilter,
  onClear,
  onApply,
}) => {
  const {
    request: searchLocationRequest,
    loading: loadingLocation,
    mappedData: locations,
  } = useApi({
    api: searchLocationWithTransaction,
    isArray: true,
    mapper: locationWithTransactionFilterResponse,
  });

  const {
    request: searchStaffRequest,
    loading: loadingStaff,
    mappedData: staffs,
  } = useApi({
    api: searchStaffWithTransaction,
    isArray: true,
    mapper: staffWithTransactionFilterResponse,
  });

  const {
    request: searchChargeRequest,
    loading: loadingCharge,
    mappedData: charges,
  } = useApi({
    api: searchAdditionalCharge,
    isArray: true,
    mapper: additionalChargeResponseMapper,
  });

  useMount(() => {
    fetchLocations(requestState);
    fetchStaffs(requestState);
    fetchCharges(requestState);
  });

  const fetchLocations = useCallback(
    (requestState) => {
      searchLocationRequest(mapObject({ ...requestState }, locationWithTransactionFilterRequest));
    },
    [searchLocationRequest]
  );

  const fetchStaffs = useCallback(
    (requestState) => {
      searchStaffRequest(mapObject({ ...requestState }, staffWithTransactionFilterRequest));
    },
    [searchStaffRequest]
  );

  const fetchCharges = useCallback(
    (requestState) => {
      searchChargeRequest(mapObject({ ...requestState }, categoryWithTransactionFilterRequest));
    },
    [searchChargeRequest]
  );

  const applyFilterCb = useCallback(
    async (searchKey) => {
      await onApply(searchKey);
    },
    [onApply]
  );

  const clearFilterCb = useCallback(() => {
    const { filterState, requestState } = clearFilter();
    onClear(filterState, requestState);
    fetchLocations(requestState);
    fetchStaffs(requestState);
    fetchCharges(requestState);
  }, [clearFilter, onClear, fetchLocations, fetchStaffs, fetchCharges]);

  const changeDateRangeCb = useCallback(
    (value) => {
      const { requestState } = modifyFilter("dateRange", { value });
      fetchLocations(requestState);
      fetchStaffs(requestState);
      fetchCharges(requestState);
    },
    [fetchLocations, fetchStaffs, fetchCharges, modifyFilter]
  );

  const locationOptions = useMemo(() => {
    return mapObjectsToSelect(locations, { textKey: "name" });
  }, [locations]);

  useEffect(() => {
    if (!locationOptions.length && filterState.locations.length) {
      modifyFilter("locations", { value: [] });
    }
  }, [locationOptions, filterState.locations, modifyFilter]);

  const staffOptions = useMemo(() => {
    let sorted = staffs
      .map((staff) => {
        staff.name = staff.name.trim();
        return staff;
      })
      .sort((a, b) => (a.name > b.name ? 1 : -1));

    return mapObjectsToSelect(sorted, { textKey: "name" });
  }, [staffs]);

  const chargeOptions = useMemo(() => {
    let sorted = charges
      .map((charge) => {
        charge.name = charge.name.trim();
        return charge;
      })
      .sort((a, b) => (a.name > b.name ? 1 : -1));

    return mapObjectsToSelect(sorted, { textKey: "name" });
  }, [charges]);

  return (
    <FilterBar
      className="my-lg"
      filterState={filterState}
      searchBox={false}
      onClear={clearFilterCb}
      onApply={applyFilterCb}
    >
      <FilterDateRange
        id="dateRange"
        label={lang.dateRange}
        maxDays={90}
        value={filterState.dateRange}
        onChange={changeDateRangeCb}
      />
      <FilterTimeRange
        id="timeRange"
        label={lang.timeRange}
        value={filterState.dateRange}
        onChange={(value) => modifyFilter("dateRange", { value })}
      />
      <FilterMultiSelect
        id="locations"
        label={lang.outlet}
        allLabel={lang.allLocations}
        emptyLabel={lang.noLocationAvailable}
        disableWhenEmpty={false}
        loading={loadingLocation}
        options={locationOptions}
        value={filterState.locations}
        onChange={(value) => modifyFilter("locations", { value })}
      />
      <FilterMultiSelect
        id="staffs"
        label={lang.staff}
        allLabel={lang.allStaff}
        emptyLabel={lang.noAvailableStaff}
        disableWhenEmpty={false}
        loading={loadingStaff}
        options={staffOptions}
        value={filterState.staffs}
        onChange={(value) => modifyFilter("staffs", { value })}
      />
      <FilterMultiSelect
        id="charges"
        label={lang.charge}
        allLabel={lang.all}
        disableWhenEmpty={false}
        loading={loadingCharge}
        options={chargeOptions}
        value={filterState.charges}
        onChange={(value) => modifyFilter("charges", { value })}
      />
    </FilterBar>
  );
};

export default AdditionalChargeReportFilter;
